import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import TextButton from 'LuxKit/Button/TextButton'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import { pluralizeToString } from 'lib/string/pluralize'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TourV2DepartureSoldOutTile from './TourV2DepartureSoldOutTile'
import { checkPurchasableOptionHasPackageUpgradeOrRoomType, getTourPricePerDay, getTourV2VariationDuration, getTourValueAtPerDay } from 'lib/tours/tourUtils'
import { min, sum } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import FormatCurrency from 'components/Common/FormatCurrency'
import TourV2DeparturePricingDropdown from './TourV2DeparturePricingDropdown'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { isCruiseFlashOfferV2 } from 'lib/offer/offerTypes'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import TourV2DepartureTileDates from './TourV2DepartureTileDates'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TourDepositTag from '../TourDepositTag'
import moment from 'moment'
import ModalContext from 'contexts/ModalContext'
import TourDepositInfoModal, { TourDepositInfoModalResult } from '../TourDepositInfoModal'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import noop from 'lib/function/noop'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { maybeWithValue } from 'lib/offer/pricing'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "tags"
  "member"
  "prices";

  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr ${rem(190)} min-content;
    grid-template-areas:
    "dates prices cta"
    "tags prices cta"
    "member prices cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(320)} 1fr ${rem(190)} min-content;
    grid-template-areas:
      "dates tags prices cta"
      "dates member prices cta";
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: ${rem(320)} ${rem(180)} ${rem(210)} ${rem(190)} min-content;
    grid-template-areas: "dates tags member prices cta";
  }
`

const TagGridArea = styled(Group)`
  grid-area: tags;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: end;
  }
`

const MemberGridArea = styled(Group)`
  grid-area: member;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: start;
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  departure: App.Tours.TourV2OfferDeparture
  travellers: Array<OfferPageRoom>
  variation: App.Tours.TourV2OfferVariation
  onSelect?: (departure: App.Tours.TourV2OfferDeparture, keepBrowsing?: boolean) => void
}
interface MappedStateProps {
  isSpoofing: boolean
}

function TourV2DepartureTile(props: Props & MappedStateProps) {
  const {
    offer,
    departure,
    onSelect = noop,
    travellers,
    variation,
  } = props

  const showModal = useContext(ModalContext)

  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const tourPriceMode = useAppSelector(getTourPriceMode)

  const isCruiseFlashOffer = isCruiseFlashOfferV2(offer)

  const views = useMemo(() => getDepartureOptionsForTravellers(departure, travellers), [travellers, departure])
  const breakdowns = useMemo(() => views.map(view => {
    return generateDepartureRoomPriceBreakdown(view.room, view.cheapestOption, offer)
  }), [offer, views])

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const total = useMemo(() => sum(breakdowns, breakdown => {
    if (canViewLuxPlusBenefits) {
      return breakdown.totalMemberPrice || breakdown.totalPrice
    }
    return breakdown.totalPrice
  }), [breakdowns, canViewLuxPlusBenefits])

  const cheapestOption = useMemo(() => min(views, view => view.cheapestOption.price)?.cheapestOption, [views])
  const onSelectClick = useCallback(() => {
    onSelect(departure)
  }, [departure, onSelect])

  const details = useMemo(() => cheapestOption ? extractTourV2PurchasableOptionDetails(cheapestOption) : undefined, [cheapestOption])
  const duration = useMemo<number | undefined>(() => {
    if (variation) {
      return getTourV2VariationDuration(variation)
    }
  }, [variation])
  const pricePerDayDetails = useMemo(() => {
    if (!details || tourPriceMode === 'total') return
    const pricePerDay = getTourPricePerDay(details.pricePerPerson, details.memberPricePerPerson, duration)
    const valueAt = getTourValueAtPerDay(details.valuedAt?.price, duration)

    if (pricePerDay) {
      return {
        pricing: maybeWithValue({
          price: pricePerDay.pricePerPersonPerDay,
          fees: [],
          saleUnit: 'day per person',
        }, details.valuedAt?.price),
        memberPricing: maybeWithValue({
          price: pricePerDay.memberPricePerPersonPerDay,
          fees: [],
          saleUnit: 'day per person',
        }, details.valuedAt?.price),
        valueAt,
      }
    }
  }, [details, duration, tourPriceMode])

  const { hasPackageUpgrade, hasRoomUpgrade } = useMemo(() => {
    return checkPurchasableOptionHasPackageUpgradeOrRoomType(views.flatMap(view => view.options))
  }, [views])

  const hasDiscount = useMemo(() => {
    return views.flatMap(view => view.options).some(option => extractTourV2PurchasableOptionDetails(option).hasDiscount)
  }, [views])

  const tourV2StartLocation = useMemo(() => [offer.variations[0]?.startLocation ?? ''], [offer])

  const isDepositAvailable = useMemo<boolean>(() => {
    if (!offer.paymentScheduleTemplate) return false
    const departureDate = moment(departure.startDate)
    const daysTillDeparture = departureDate.diff(moment(), 'days')
    return daysTillDeparture >= offer.paymentScheduleTemplate.minimumLeadDays
  }, [departure, offer])

  const handleTourDepositInfoClick = useCallback(async() => {
    if (offer.paymentScheduleTemplate) {
      const result = await showModal<TourDepositInfoModalResult>(<TourDepositInfoModal
        paymentScheduleTemplate={offer.paymentScheduleTemplate}
        primaryActionText="Select departure"
      />)
      if (result === 'primary-action') {
        onSelectClick()
      }
    }
  }, [offer.paymentScheduleTemplate, onSelectClick, showModal])

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestOption?.price,
            memberPrice: cheapestOption?.memberPrice,
            roomType: cheapestOption?.roomType,
            packageId: cheapestOption?.fkDepartureId,
            offerId: cheapestOption?.fkTourId,
          }),
      ]
    },
    [cheapestOption, offer],
  )

  if (!cheapestOption || !details) {
    return <TourV2DepartureSoldOutTile
      departure={departure}
      offer={offer}
      travellers={travellers}
    />
  }

  return <Tile>
    <Group direction="vertical" gap={12}>
      <TourV2DepartureTileDates departure={departure} gridArea="dates" />
      <LoyaltyPointsEarnWithBreakdownModal calculationRequests={pointsEarnCalculationOptions} calculationType="estimate" />
      {props.isSpoofing && <CaptionTextBlock variant="medium" weight="bold" colour="urgency">
        Days till Handback: {moment(departure.handbackDate).diff(moment(), 'days')}
      </CaptionTextBlock>}
    </Group>
    <TagGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      tabletGap={4}
      wrap="wrap"
    >
      {!isCruiseFlashOffer && <>
        {departure.isGuaranteed && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidCheckCircleIcon colour="highlight-secondary" />}
          endIcon={<Tooltip description="Guaranteed with 2 passengers">
            <LineInfoCircleIcon colour="neutral-two" />
          </Tooltip>}
        >
          Guaranteed departure
        </CaptionTextBlock>}
        {cheapestOption.inventoryLeft <= 4 && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidExclamationCircleIcon colour="urgency" />}
        >
          {pluralizeToString('spot', cheapestOption.inventoryLeft)} left
        </CaptionTextBlock>}
        {(hasPackageUpgrade || hasRoomUpgrade) && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidCheckCircleIcon colour="highlight-secondary" />}
        >
          Upgrades available
        </CaptionTextBlock>}
        {cheapestOption.inventoryLeft > 4 && props.isSpoofing && <CaptionTextBlock variant="medium" weight="bold" colour="urgency">
          {cheapestOption.inventoryLeft} {cheapestOption.roomType} spots left
        </CaptionTextBlock>}
      </>}
    </TagGridArea>
    <MemberGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      desktopGap={4}
      wrap="wrap"
    >
      {isDepositAvailable && <TourDepositTag offer={offer} onInfoClick={handleTourDepositInfoClick} />}
      {hasDiscount && <TourDiscountLabel />}
    </MemberGridArea>
    <Group
      style={{ gridArea: 'prices' }}
      direction="horizontal"
      gap={12}
      verticalAlign="end"
      horizontalAlign="space-between"
    >
      <VerticalSpacer gap={4}>
        <div>
          <PriceRowPriceCaption>From</PriceRowPriceCaption>
          {!pricePerDayDetails && <LuxPlusPriceStack
            size="L"
            pricing={details.pricing}
            memberPricing={details.memberPricing}
            isTour
          />}
          {pricePerDayDetails && <LuxPlusPriceStack
            size="L"
            pricing={pricePerDayDetails.pricing}
            memberPricing={pricePerDayDetails.memberPricing}
            isTour
          />}
          {details.hasDiscount && !(canViewLuxPlusBenefits && details.memberPrice) && <PriceRowSale value={details.fullPricePerPerson} />}
          <PriceRowAgentHubCommission
            size="M"
            kind="plain"
            offerLocations={tourV2StartLocation}
            offerId={offer.id}
            productType={offer.productType}
          />
          {!!details.valuedAt && <>
            {!pricePerDayDetails?.valueAt && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={details.valuedAt.price}
              discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
            />}
            {!!pricePerDayDetails?.valueAt && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={pricePerDayDetails?.valueAt}
              discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
            />}
          </>}
          <PriceRowCaption>
            {travellers.length <= 1 && details.roomTypeName}
            {travellers.length > 1 && <>
              <Plural count={travellers.length} singular="room" withCount /> · <FormatCurrency value={total} /> total
            </>}
          </PriceRowCaption>
        </div>
        {travellers.length > 1 && <TourV2DeparturePricingDropdown
          travellers={travellers}
          departure={departure}
          offer={offer}
          variation={variation}
        /> }
      </VerticalSpacer>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" gap={8}>
          <TextButton kind="primary" onClick={onSelectClick}>Select</TextButton>
        </Group>
      </CSSBreakpoint>
    </Group>
    <CSSBreakpoint min="tablet" style={{ gridArea: 'cta' }}>
      <Group direction="vertical" gap={8}>
        <TextButton kind="primary" onClick={onSelectClick} data-testid="departure-book-button">Select</TextButton>
      </Group>
    </CSSBreakpoint>
  </Tile>
}
export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    isSpoofing: state.auth.account.isSpoofed,
  }
})(React.memo(TourV2DepartureTile))
